import React from 'react';
import { useNavigate } from 'react-router-dom';
import { PageLayout } from '@/shared/layout';
import { Button, Filter, Search, KPICard, Card, Badge, Table } from '@/components/ui';
import { useProjectPhases } from './hooks/usePhases';
import { useAuth } from '@/core/auth/AuthContext';
import { AuthButton } from '@/core/permissions/AuthButton';
import type { PhaseStatus, ConstructionPhaseType } from './types/project-phases.types';

const statusColors: Record<PhaseStatus, string> = {
  draft: 'bg-slate-100 text-slate-600',
  planned: 'bg-blue-50 text-blue-500',
  ready: 'bg-blue-100 text-blue-600',
  in_progress: 'bg-amber-100 text-amber-600',
  blocked: 'bg-red-100 text-red-600',
  qa_pending: 'bg-indigo-100 text-indigo-600',
  completed: 'bg-emerald-100 text-emerald-600',
  handover_pending: 'bg-teal-100 text-teal-600',
  closed: 'bg-slate-900 text-white',
  cancelled: 'bg-slate-400 text-white',
};

const statusLabels: Record<PhaseStatus, string> = {
  draft: 'Bản thảo',
  planned: 'Lên kế hoạch',
  ready: 'Sẵn sàng',
  in_progress: 'Đang triển khai',
  blocked: 'Tạm dừng',
  qa_pending: 'Chờ nghiệm thu',
  completed: 'Hoàn thành',
  handover_pending: 'Chờ bàn giao',
  closed: 'Đã đóng',
  cancelled: 'Đã hủy',
};

const typeLabels: Record<ConstructionPhaseType, string> = {
  site_preparation: 'Chuẩn bị mặt bằng',
  earthwork_grading: 'San nền / Tạo địa hình',
  soil_preparation: 'Xử lý đất trồng',
  irrigation_installation: 'Hệ thống tưới',
  lighting_installation: 'Đèn cảnh quan',
  hardscape_edge: 'Bó vỉa / Hardscape',
  tree_planting: 'Trồng cây bóng mát',
  shrub_groundcover: 'Cây bụi / Phủ nền',
  turf_lawn: 'Trải cỏ',
  fertilizing_mulching: 'Phân bón / Phủ gốc',
  testing_commissioning: 'Vận hành thử (T&C)',
  defect_rectification: 'Sửa lỗi',
  handover: 'Bàn giao',
};

export default function PhaseListPage() {
  const { profile } = useAuth();
  const companyId = profile?.company_id || '';
  const navigate = useNavigate();

  const {
    phases,
    stats,
    isLoading,
    filters,
    setFilters,
  } = useProjectPhases(companyId);

  return (
    <PageLayout
      title="Giai đoạn Thi công (Phases)"
      description="Quản lý trình tự triển khai dự án, các mốc thanh toán và tiến độ tổng thể của các giai đoạn thi công cảnh quan."
      breadcrumbs={[{ label: 'Dự án' }, { label: 'Giai đoạn' }]}
      isLoading={isLoading}
      headerActions={
        <div className="flex gap-2">
          <Button variant="outline" icon="ri-calendar-todo-line">Xem Gantt Chart</Button>
          <AuthButton
            module="projects"
            action="create"
            variant="primary"
            icon="ri-add-line"
            onClick={() => navigate('./create')}
          >
            Thêm giai đoạn
          </AuthButton>
        </div>
      }
    >
      <div className="space-y-6">
        {/* KPI Board */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <KPICard
            title="Tiến độ trung bình"
            value={Math.round(stats?.avgProgress || 0)}
            icon="ri-line-chart-line"
            variant="primary"
            suffix="%"
          />
          <KPICard
            title="Giai đoạn chậm trễ"
            value={stats?.delayedPhases || 0}
            icon="ri-time-warning-line"
            variant="danger"
          />
          <KPICard
            title="Mốc thanh toán"
            value={stats?.paymentMilestones || 0}
            icon="ri-money-dollar-circle-line"
            variant="success"
          />
          <KPICard
            title="Hoàn thành"
            value={stats?.completedPhases || 0}
            icon="ri-checkbox-circle-line"
            variant="info"
          />
        </div>

        <PageLayout.Toolbar>
          <div className="flex flex-col md:flex-row gap-4 w-full justify-between items-start md:items-center">
            <div className="flex flex-wrap gap-3 flex-1">
              <Search
                placeholder="Tìm mã giai đoạn, tên giai đoạn..."
                value={filters.search}
                onSearch={(v) => setFilters({ ...filters, search: v })}
                className="w-full md:w-80"
              />
              <Filter
                options={[
                  {
                    id: 'status', label: 'Trạng thái', type: 'select', choices: [
                      { value: 'all', label: 'Tất cả trạng thái' },
                      ...Object.entries(statusLabels).map(([val, label]) => ({ value: val, label }))
                    ]
                  },
                  {
                    id: 'type', label: 'Loại hình', type: 'select', choices: [
                      { value: 'all', label: 'Tất cả loại' },
                      ...Object.entries(typeLabels).map(([val, label]) => ({ value: val, label }))
                    ]
                  }
                ]}
                onFilterChange={(k, v) => setFilters({ ...filters, [k]: v })}
              />
            </div>
          </div>
        </PageLayout.Toolbar>

        <Card className="overflow-hidden border-none shadow-sm">
          <Table
            columns={[
              { key: 'code', title: 'Mã hiệu' },
              { key: 'name', title: 'Tên giai đoạn & Phân loại' },
              { key: 'dates', title: 'Kế hoạch' },
              { key: 'progress', title: 'Tiến độ', align: 'center' },
              { key: 'milestones', title: 'Đặc tính', align: 'center' },
              { key: 'status', title: 'Trạng thái', align: 'center' },
            ]}
            data={phases}
            isLoading={isLoading}
            onRowClick={(item) => navigate(`./detail/${item.id}`)}
            renderCell={(item, key) => {
              switch (key) {
                case 'code':
                  return <span className="font-mono text-[11px] font-bold text-os-primary tracking-wider">{item.code}</span>;
                case 'name':
                  return (
                    <div className="flex flex-col">
                      <span className="font-bold text-slate-900 line-clamp-1">{item.name}</span>
                      <div className="flex items-center gap-1.5 mt-0.5">
                        <span className="text-[10px] text-slate-400 font-bold">{typeLabels[item.phase_type]}</span>
                        <span className="text-slate-300">•</span>
                        <span className="text-[10px] text-slate-400 font-medium">{item.project?.name}</span>
                      </div>
                    </div>
                  );
                case 'dates':
                  return (
                    <div className="flex flex-col">
                      <span className="sm:text-sm text-xs font-normal text-slate-700">{item.planned_start_date ? new Date(item.planned_start_date).toLocaleDateString('vi-VN') : '---'}</span>
                      <span className="text-[10px] text-slate-400 italic">đến {item.planned_end_date ? new Date(item.planned_end_date).toLocaleDateString('vi-VN') : '---'}</span>
                    </div>
                  );
                case 'progress':
                  return (
                    <div className="flex flex-col items-center">
                      <div className="w-16 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full bg-os-primary transition-all`}
                          style={{ width: `${item.progress_percent}%` }}
                        />
                      </div>
                      <span className="text-[10px] font-bold text-slate-500 mt-1">{item.progress_percent}%</span>
                    </div>
                  );
                case 'milestones':
                  return (
                    <div className="flex gap-1 justify-center">
                      {item.payment_milestone && <div className="w-5 h-5 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center" title="Mốc thanh toán"><i className="ri-money-dollar-circle-line sm:text-sm text-xs" /></div>}
                      {item.qa_required && <div className="w-5 h-5 rounded-full bg-indigo-50 text-indigo-600 flex items-center justify-center" title="Yêu cầu QA"><i className="ri-shield-check-line sm:text-sm text-xs" /></div>}
                      {item.handover_required && <div className="w-5 h-5 rounded-full bg-teal-50 text-teal-600 flex items-center justify-center" title="Yêu cầu bàn giao"><i className="ri-key-line sm:text-sm text-xs" /></div>}
                    </div>
                  );
                case 'status':
                  return (
                    <Badge className={`${statusColors[item.status_code]} border-transparent text-[10px] font-bold`}>
                      {statusLabels[item.status_code]}
                    </Badge>
                  );
                default:
                  return null;
              }
            }}
          />
        </Card>
      </div>
    </PageLayout>
  );
}
